/* global L:false */
import angular from 'angular';
import { compact, forEach, get, merge, round } from 'lodash';

module.exports = angular.module('common.directives.od-comps-map', [
  require('../filters/currency'),
  require('../filters/letterize'),
  require('angular-animate'),
])
.directive('odCompsMap', function(currencyFilter, letterizeFilter, MapboxConfig, $animate, $mediaQuery) { // eslint-disable-line max-len
  return {
    restrict: 'EA',
    scope: {
      ngModel: '=',
      hoveredCompIndex: '=?',
      subjectAddress: '=',
      subdivision: '='
    },
    link: function(scope, element, attrs) {
      var map = null
        , subjectPosition = null
        , markers = {}
        , container = element.append('<div>').find('div').css({ 'height': '100%' });

      scope.$watchGroup([attrs.ngModel, attrs.subjectAddress], function() {
        rebuildMap();
      });

      function rebuildMap() {
        var comps = scope.ngModel
          , subjectAddress = scope.subjectAddress
          , subdivision = scope.subdivision;
        if (!comps || !subjectAddress) return;

        // Clear out any remnants
        container[0].innerHTML = '';

        subjectPosition = [parseFloat(subjectAddress.latitude), parseFloat(subjectAddress.longitude)]; // eslint-disable-line max-len
        L.mapbox.accessToken = MapboxConfig.accessToken;
        L.mapbox.config.FORCE_HTTPS = true;
        map = L.mapbox.map(element.children()[0], MapboxConfig.defaultMap, {
          attributionControl: { compact: true },
          boxZoom: false,
          center: subjectPosition,
          doubleClickZoom: true,
          dragging: false,
          keyboard: false,
          scrollWheelZoom: false,
          tap: false,
          touchZoom: true,
          zoom: 14,
          zoomControl: false,
        });

        if ($mediaQuery('gt-xs')) {
          map.dragging.enable();
          if (map.tap) { // doesn't exist on non-touch devices
            map.tap.enable();
          }
          new L.Control.Zoom().addTo(map);
        }

        const subdivisionLayer = L.mapbox.featureLayer().addTo(map);
        subdivisionLayer.setGeoJSON({
          type: 'FeatureCollection',
          features: [{
            type: 'Feature',
            properties: {
              'stroke': 'black',
              'stroke-opacity': 0.5,
              'fill': 'black',
              'fill-opacity': 0.1
            },
            geometry: subdivision
          }]
        });

        var pinLayer = makeCompLayer().addTo(map)
          , compGeoJson = [];

        const subjectMarkerContents = `<span class='ellipsis'>${subjectAddress.street}</span>`;
        const subjectGeoJson = makeMarker(subjectPosition, subjectMarkerContents);
        compGeoJson.push(subjectGeoJson);

        forEach(compact(comps), function(comp, compIndex) {
          let lat = get(comp, ['address', 'latitude']) ||
            get(comp, ['address_info', 'address', 'latitude']) ||
            get(comp, ['listing', 'coordinates', 'lat']);
          let lng = get(comp, ['address', 'longitude']) ||
            get(comp, ['address_info', 'address', 'longitude']) ||
            get(comp, ['listing', 'coordinates', 'lng']);
          let priceCents = get(comp, ['listing_data', 'price_cents']) ||
            get(comp, ['listing', 'price_cents']);

          if (!lat || !lng) return;

          // Don't show comp markers if in the same area as subject location (e.g. for condo comps)
          if (
            round(lat, 3) === round(subjectAddress.latitude, 3) &&
            round(lng, 3) === round(subjectAddress.longitude, 3)
          ) return;

          var marker = makeMarker(
            [lat, lng],
            `${currencyFilter(priceCents, -3)}`, // i.e. $400K
            comp,
            compIndex
          );
          compGeoJson.push(marker);
        });

        pinLayer.setGeoJSON({
          type: 'FeatureCollection',
          features: compGeoJson,
        });
        map.fitBounds(pinLayer.getBounds(), {
          animate: false,
          paddingBottomRight: [50, 30],
          paddingTopLeft: [50, 50]
        });
      }

      function makeCompLayer() {
        var pinLayer = L.mapbox.featureLayer();
        pinLayer.on('layeradd', function(e) {
          var marker = e.layer
            , feature = marker.feature
            , comp = feature.properties.comp
            , compIndex = feature.properties.compIndex;

          marker.hoverOn = function() {
            var iconOpts = merge({}, feature.properties.icon, {
              className: feature.properties.icon.className + ' hover',
            });
            marker.setIcon(L.divIcon(iconOpts));
            marker.setZIndexOffset(2000);
            if (marker.div)
              $animate.addClass(marker.div, 'hover');
          };

          marker.hoverOff = function() {
            marker.setIcon(L.divIcon(feature.properties.icon));
            marker.setZIndexOffset(null); // null is required here, not undefined
            if (marker.div)
              $animate.removeClass(marker.div, 'hover');
          };

          marker.onmouseenter = function() {
            scope.$apply(function() {
              if (compIndex) scope.hoveredCompIndex = compIndex;
            });
          };

          marker.onmouseleave = function() {
            scope.$apply(function() {
              scope.hoveredCompIndex = null;
            });
          };

          if (!marker.div && comp) {
            markers[compIndex] = marker;
          }

          if (feature.properties.isSubject)
            marker.setZIndexOffset(1000); // put subject on top of all other markers

          marker.setIcon(L.divIcon(feature.properties.icon));
        });

        pinLayer.on('mouseover', function(e) {
          var marker = e.layer;
          marker.hoverOn();
          marker.onmouseenter();
        });
        pinLayer.on('mouseout', function(e) {
          var marker = e.layer;
          marker.hoverOff();
          marker.onmouseleave();
        });

        return pinLayer;
      }

      function makeMarker(latlon, text, comp, compIndex) {
        let className = 'price-icon';
        className += comp ? ' comp' : ' subject';

        let html = text;
        let compLabel = letterizeFilter(compIndex);
        if (compLabel) {
          html = `<span class='comp-label'>${compLabel}</span>${html}`;
        }

        return {
          'type': 'Feature',
          'geometry': {
            'type': 'Point',
            'coordinates': [latlon[1], latlon[0]],
          },
          'properties': {
            'icon': {
              'className': className,
              'html': html,
              'iconSize': null,
            },
            'comp': comp,
            'compIndex': compIndex,
            'isSubject': !comp,
          }
        };
      }

      scope.$watch('hoveredCompIndex', function(newCompIndex, previous) {
        if (markers[previous])
          markers[previous].hoverOff();

        if (markers[newCompIndex])
          markers[newCompIndex].hoverOn();
      });
    }
  };
})
.name;



/** WEBPACK FOOTER **
 ** ./javascripts/common/directives/od-comps-map.js
 **/